import { Box, Button, Grid, Typography } from '@mui/material'
import { Controller, useForm, useWatch } from 'react-hook-form'
import {
    IRestaurantSetup,
    useCreateCompanyContext,
} from '../CreateCompanyContext'
import NextButton from './NextButton'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import OWSwitch from 'components/OWForm/OWSwitch'
import OWRadio from 'components/OWForm/OWRadio'
import Loading from 'components/Loading/Loading'
import getOverflowSectionSX from '../utils/getOverflowSectionSX'
import { useMemo } from 'react'
import { TOption } from 'components/OWForm/OWSelect'
import OWAutocomplete from 'components/OWForm/OWAutocomplete'
import { sortBy } from 'lodash-es'
import ReCAPTCHA from 'react-google-recaptcha'

const validate = yup.object().shape({
    currencyId: yup.number().required().min(1, 'please select currency *'),
    recaptchaCode: yup.string().required(),
})

function RestaurantSetup() {
    const {
        onPrev,
        restaurantSetup,
        currencies,
        countries,
        restaurantInfo,
        registerLoading,
        onSubmitRestaurantSetup,
    } = useCreateCompanyContext()

    const getDefaultCurrency = (): number => {
        const countryId = restaurantInfo.address.countryID
        return countries.find((i) => i.countryID === countryId)?.currencyID || 0
    }

    const currencyOptions = useMemo(
        () =>
            sortBy(
                currencies.map((c) => {
                    return {
                        value: c.currencyID,
                        label: `${c.name} (${c.symbol})`,
                    } as TOption
                }),
                (val) => val.label,
            ),
        [currencies],
    )

    const form = useForm<IRestaurantSetup>({
        defaultValues: {
            ...restaurantSetup,
            currencyId: restaurantSetup.currencyId || getDefaultCurrency(),
            // Ireland(102) and Australia(14) Tax Inclusive = TRUE
            isTaxInclusive: [102, 14].includes(restaurantInfo.address.countryID)
                ? true
                : false,
        },
        resolver: yupResolver(validate),
    })

    const { t } = useTranslation()

    const { control } = form

    const [recaptchaCode] = useWatch({
        control,
        name: ['recaptchaCode'],
    })

    const onSubmit = (data: IRestaurantSetup) => {
        onSubmitRestaurantSetup(data)
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid
                container
                spacing={2}
                sx={getOverflowSectionSX}
                textAlign="left"
            >
                <Grid item xs={12}>
                    <OWAutocomplete
                        control={control}
                        name="currencyId"
                        options={currencyOptions}
                        variant="filled"
                        label={t('register.label.currency')}
                        sx={{
                            backgroundColor: 'background.paper',
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                        }}
                    />
                </Grid>
                <Grid xs={12} item>
                    <OWSwitch
                        control={control}
                        label={
                            <Typography color="#fff">
                                <b>{t('register.label.tax_inclusive')}</b>
                            </Typography>
                        }
                        name={'isTaxInclusive'}
                        color="info"
                    />
                </Grid>
                <Grid xs={12} item>
                    <OWRadio
                        label={
                            <Box
                                sx={{
                                    color: '#fff',
                                    fontWeight: 'bold',
                                }}
                            >
                                {t('register.label.demo_data')}
                            </Box>
                        }
                        control={control}
                        name={'demoData'}
                        options={[
                            { label: 'Yes', value: 'yes' },
                            { label: 'No', value: 'no' },
                        ]}
                        color="info"
                        row
                        radioSx={{
                            color: (t) => t.palette.info.main,
                        }}
                        sx={{
                            color: '#fff',
                        }}
                    />
                </Grid>
                <Grid xs={12} item textAlign="center">
                    <Controller
                        control={control}
                        name={'recaptchaCode'}
                        render={({ field }) => (
                            <ReCAPTCHA
                                sitekey="6Lcps8AqAAAAAMyhwqYDDmDjV4vAljvhET9PrDpJ"
                                onChange={field.onChange}
                            />
                        )}
                    />
                </Grid>
                <Grid xs={12} item textAlign="center">
                    {registerLoading ? (
                        <Loading />
                    ) : (
                        <Button
                            color="info"
                            disableElevation
                            className="textUpper"
                            type="submit"
                            size="large"
                            disabled={!recaptchaCode}
                        >
                            {t('register.button.label.save_and_continue')}
                        </Button>
                    )}
                </Grid>
            </Grid>

            <Box textAlign={'center'} width={'100%'} mt={2}>
                {!registerLoading && (
                    <NextButton text={'Prev'} isPrevious onClick={onPrev} />
                )}
            </Box>
        </form>
    )
}

export default RestaurantSetup
